import React from 'react';
import {
  Box,
  HStack,
  VStack,
  Text,
  Badge,
  Progress,
  Tooltip,
  Icon,
  useColorModeValue,
} from '@chakra-ui/react';
import { FiTrendingUp, FiStar } from 'react-icons/fi';

/**
 * Contact Score Card
 * 
 * Displays contact's RFM score with breakdown.
 * Color-coded by score tier:
 * - 80-100: Hot (Red)
 * - 60-79: Warm (Orange)
 * - 40-59: Medium (Yellow)
 * - 20-39: Cool (Blue)
 * - 0-19: Cold (Gray)
 */
export default function ContactScoreCard({ contact, size = 'sm', showBreakdown = false }) {
  const score = contact?.score || 0;
  const scoreBreakdown = contact?.scoreBreakdown || {};

  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  const getScoreTier = (score) => {
    if (score >= 80) return { label: 'Hot', color: 'red' };
    if (score >= 60) return { label: 'Warm', color: 'orange' };
    if (score >= 40) return { label: 'Medium', color: 'yellow' };
    if (score >= 20) return { label: 'Cool', color: 'blue' };
    return { label: 'Cold', color: 'gray' };
  };

  const tier = getScoreTier(score);

  if (size === 'sm') {
    return (
      <Tooltip 
        label={
          <VStack spacing={1} align="flex-start">
            <Text fontSize="xs" fontWeight="bold">Contact Score: {score}/100</Text>
            <Text fontSize="xs">Tier: {tier.label}</Text>
            {showBreakdown && (
              <>
                <Text fontSize="xs">Recency: {scoreBreakdown.recency || 0}</Text>
                <Text fontSize="xs">Frequency: {scoreBreakdown.frequency || 0}</Text>
                <Text fontSize="xs">Value: {scoreBreakdown.monetary || 0}</Text>
                <Text fontSize="xs">Engagement: {scoreBreakdown.engagement || 0}</Text>
              </>
            )}
          </VStack>
        }
        hasArrow
      >
        <Badge colorScheme={tier.color} fontSize="xs">
          <HStack spacing={1}>
            <Icon as={FiStar} boxSize={2} />
            <Text>{score}</Text>
          </HStack>
        </Badge>
      </Tooltip>
    );
  }

  return (
    <Box
      p={4}
      bg={bgColor}
      borderWidth="1px"
      borderColor={borderColor}
      borderRadius="md"
    >
      <VStack align="stretch" spacing={3}>
        <HStack justify="space-between">
          <VStack align="flex-start" spacing={0}>
            <Text fontSize="xs" color="gray.600">Contact Score</Text>
            <HStack spacing={2}>
              <Text fontSize="2xl" fontWeight="bold">{score}</Text>
              <Badge colorScheme={tier.color}>{tier.label}</Badge>
            </HStack>
          </VStack>
          <Icon as={FiTrendingUp} boxSize={6} color={`${tier.color}.500`} />
        </HStack>

        <Progress value={score} colorScheme={tier.color} borderRadius="full" size="sm" />

        {showBreakdown && (
          <VStack align="stretch" spacing={2} mt={2}>
            <Text fontSize="xs" fontWeight="semibold">Score Breakdown:</Text>
            <HStack justify="space-between">
              <Text fontSize="xs" color="gray.600">Recency</Text>
              <Badge size="xs">{scoreBreakdown.recency || 0}</Badge>
            </HStack>
            <HStack justify="space-between">
              <Text fontSize="xs" color="gray.600">Frequency</Text>
              <Badge size="xs">{scoreBreakdown.frequency || 0}</Badge>
            </HStack>
            <HStack justify="space-between">
              <Text fontSize="xs" color="gray.600">Monetary Value</Text>
              <Badge size="xs">{scoreBreakdown.monetary || 0}</Badge>
            </HStack>
            <HStack justify="space-between">
              <Text fontSize="xs" color="gray.600">Engagement</Text>
              <Badge size="xs">{scoreBreakdown.engagement || 0}</Badge>
            </HStack>
          </VStack>
        )}
      </VStack>
    </Box>
  );
}

